<script lang="ts">
  import { formatDays } from '../model/format';

  export interface StageDuration {
    label: string;
    days: number;
  }

  export function formatStageTooltip(stage: StageDuration): string {
    return `${stage.label}: ${formatDays(stage.days)}`;
  }

  const AXIS_STEP = 2;
  const MIN_AXIS_MAX = 8;
  const MAX_AXIS_INTERVALS = 6;

  export function computeAxisTicks(maxDays: number): number[] {
    const safeMax = Number.isFinite(maxDays) ? Math.max(maxDays, 0) : 0;
    const step =
      AXIS_STEP *
      Math.ceil(Math.max(safeMax, MIN_AXIS_MAX) / (AXIS_STEP * MAX_AXIS_INTERVALS));
    const axisMax = Math.max(MIN_AXIS_MAX, step * Math.ceil(safeMax / step));
    const ticks: number[] = [];
    for (let tick = 0; tick <= axisMax; tick += step) {
      ticks.push(tick);
    }
    return ticks;
  }
</script>

<script setup lang="ts">
  import { computed } from 'vue';

  interface Props {
    stages: StageDuration[];
  }

  const props = defineProps<Props>();

  const CHART_COLOR_COUNT = 5;

  const ticks = computed(() => {
    const maxDays = Math.max(0, ...props.stages.map((stage) => stage.days));
    return computeAxisTicks(maxDays);
  });

  const axisMax = computed(() => ticks.value[ticks.value.length - 1]);

  const ariaLabel = computed(() => {
    const stages = props.stages
      .map((stage) => `${stage.label}: ${formatDays(stage.days)}`)
      .join('; ');
    return `${stages}; eixo de 0 a ${formatDays(axisMax.value)}`;
  });

  const valueReserve = computed(() => {
    const maxChars = Math.max(0, ...props.stages.map((stage) => formatDays(stage.days).length));
    return `calc(${maxChars}ch + 0.5rem)`;
  });

  function tickLeft(tick: number): string {
    return `${(tick / axisMax.value) * 100}%`;
  }

  function barWidth(days: number): string {
    const ratio = Math.min(Math.max(days / axisMax.value, 0), 1);
    return `calc((100% - ${valueReserve.value}) * ${ratio})`;
  }

  function barColor(index: number): string {
    return `var(--chart-${Math.min(index + 1, CHART_COLOR_COUNT)})`;
  }
</script>

<template>
  <div
    data-testid="stage-duration-bars"
    role="img"
    :aria-label="ariaLabel"
  >
    <div class="relative h-[66px]">
      <div
        class="pointer-events-none absolute inset-y-0 left-0"
        :style="{ right: valueReserve }"
        aria-hidden="true"
      >
        <span
          v-for="tick in ticks"
          :key="tick"
          data-testid="stage-grid-line"
          class="absolute inset-y-0 w-px bg-gray-02"
          :style="{ left: tickLeft(tick) }"
        />
      </div>
      <div class="relative flex h-full flex-col justify-center gap-1.5">
        <div
          v-for="(stage, index) in props.stages"
          :key="stage.label"
          data-testid="stage-row"
          class="flex items-center"
        >
          <div
            v-tippy="{
              content: formatStageTooltip(stage),
              placement: 'top',
              theme: 'custom',
            }"
            data-testid="stage-bar"
            class="bar-grow-x relative h-[26px] shrink-0 overflow-hidden rounded"
            :style="{ width: barWidth(stage.days), backgroundColor: barColor(index) }"
          >
            <span class="absolute inset-x-2 inset-y-0 flex items-center overflow-hidden">
              <span class="min-w-0 truncate text-xs text-white">{{ stage.label }}</span>
            </span>
          </div>
          <span
            data-testid="stage-days"
            class="ml-2 shrink-0 whitespace-nowrap text-xs text-gray-07"
          >
            {{ formatDays(stage.days) }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="relative h-5"
      :style="{ marginRight: valueReserve }"
      aria-hidden="true"
    >
      <span
        v-for="(tick, index) in ticks"
        :key="tick"
        data-testid="stage-axis-tick"
        class="absolute top-0 whitespace-nowrap text-[10px]/5 text-gray-03"
        :class="index === 0 ? '' : '-translate-x-1/2'"
        :style="{ left: tickLeft(tick) }"
      >
        {{ formatDays(tick) }}
      </span>
    </div>
  </div>
</template>

<style scoped>
  @media (prefers-reduced-motion: no-preference) {
    .bar-grow-x {
      transition: width 400ms ease-out;
      animation: bar-grow-x 400ms ease-out;
    }

    @keyframes bar-grow-x {
      from {
        width: 0;
      }
    }
  }
</style>
